import React, { Component } from 'react'
import { Alert, Button, Collapse, Input, Spin, Tag, Typography, message } from 'antd'
import {
    AppleOutlined,
    ArrowRightOutlined,
    CheckCircleOutlined,
    CheckOutlined,
    CloudDownloadOutlined,
    CodeOutlined,
    CopyOutlined,
    RocketOutlined,
    SafetyCertificateOutlined,
    SendOutlined,
    SyncOutlined,
    ToolOutlined,
    WindowsOutlined,
} from '@ant-design/icons'
import { getA2aPortalOverview } from '../../request'
import macosGatekeeperStillOpen from './assets/macos-gatekeeper-still-open.jpg'

const DOWNLOADS = [
    {
        key: 'macos-arm64',
        icon: <AppleOutlined />,
        platform: 'macOS · Apple 芯片',
        detail: '适用于 M1、M2、M3、M4 等 Apple Silicon Mac',
    },
    {
        key: 'macos-x64',
        icon: <AppleOutlined />,
        platform: 'macOS · Intel 芯片',
        detail: '适用于使用 Intel 处理器的 Mac',
    },
    {
        key: 'windows-x64',
        icon: <WindowsOutlined />,
        platform: 'Windows · 64 位',
        detail: '适用于公司常用的 Windows 10 / 11 电脑',
    },
]

const PRODUCT_CAPABILITIES = [
    {
        icon: <SendOutlined />,
        title: '任务直接进入 Codex',
        description: '任务正文、项目、执行人、优先级、截止日期和附件一起送达，不再从聊天记录中拼凑上下文。',
    },
    {
        icon: <CodeOutlined />,
        title: '一键创建原生会话',
        description: '使用官方 Codex 原生机制创建会话，并校验任务内容和本机项目是否真实关联成功。',
    },
    {
        icon: <SyncOutlined />,
        title: '团队状态持续同步',
        description: '从接收任务、创建会话到人工确认完成，关键状态持续回传，减少反复询问处理进度。',
    },
    {
        icon: <ToolOutlined />,
        title: '常用能力集中管理',
        description: '会话、公司 Skills、快捷按钮、用户脚本、版本更新和诊断，都集中在 Codex 工具箱中。',
    },
]

const COMPARISONS = [
    ['在聊天工具里发送一段需求', '通过 A2A 派发结构化任务'],
    ['手工复制标题、正文和附件', '完整任务上下文直接送达'],
    ['接收人重新整理 Codex 提示词', '核查后一键创建原生会话'],
    ['服务端项目与本机目录容易混淆', '通过项目映射准确关联'],
    ['通过聊天反复询问处理进度', '任务状态自动同步给派发方'],
    ['Codex 回复结束就被当作完成', '检查实际结果后人工确认完成'],
]

const SIMPLE_FLOW = [
    { number: '01', title: '安装并登录', description: '选择适合电脑的安装包，使用乐纯员工账号完成登录。' },
    { number: '02', title: '启用 A2A', description: '通过引导配置 lechun-a2a MCP，完成连接检查。' },
    { number: '03', title: '收发任务', description: '核查任务内容，选择本机 Codex 项目并创建会话。' },
    { number: '04', title: '确认完成', description: '检查代码和实际结果，再将完成状态同步给团队。' },
]

const INSTALL_STEPS = [
    {
        title: '下载并安装 CodexPlus',
        lead: '根据电脑系统选择对应版本，下载后完成安装，整个过程通常只需要几分钟。',
        actions: [
            'Mac 用户点击“苹果菜单 → 关于本机”，确认是 Apple 芯片还是 Intel 芯片；Windows 用户选择 Windows 64 位版。',
            '前往本页下载模块，选择与电脑系统和芯片匹配的 CodexPlus 安装包。',
            'macOS 用户打开 DMG，将 CodexPlus 拖入“应用程序”；Windows 用户运行安装程序并按照提示完成安装。',
            '安装完成后，确认可以在“应用程序”或开始菜单中找到 CodexPlus。',
        ],
        finish: '已经在电脑中找到 CodexPlus，可以开始首次启动。',
        tip: '只从乐纯 Portal 下载统一发布的安装包；macOS 用户不要长期直接从 DMG 安装镜像中运行 CodexPlus。',
        downloadLink: true,
    },
    {
        title: '完全退出 Codex 并首次启动',
        lead: 'CodexPlus 需要从启动阶段把增强能力加载到官方 Codex。',
        actions: [
            '完全退出已经运行的官方 Codex。',
            '从“应用程序”打开 CodexPlus。',
            '等待 CodexPlus 自动启动官方 Codex。',
        ],
        finish: '官方 Codex 被自动打开，并出现 CodexPlus 登录流程。',
        tip: '不要先打开官方 Codex，再启动 CodexPlus；如果已经打开，请完全退出后重试。',
    },
    {
        title: '登录公司账号',
        lead: '登录成功后，CodexPlus 会自动维护公司服务所需的认证信息。',
        actions: [
            '使用乐纯员工用户名和密码登录。',
            '也可以点击蓝色按钮，在系统浏览器中完成钉钉授权。',
            '浏览器授权完成后等待 CodexPlus 自动登录并启动服务。',
        ],
        finish: '登录成功，A2A 引导中的“登录状态”显示已完成。',
        tip: '不需要手工复制 API Key，也不需要配置环境变量。',
    },
    {
        title: '确认 CodexPlus 工具箱成功加载',
        lead: '登录后回到官方 Codex，确认页面增强功能已经加载。',
        actions: [
            '等待官方 Codex 主页面完成加载。',
            '在页面右上方找到 CodexPlus 工具箱按钮。',
            '点击按钮，确认可以打开 CodexPlus 面板。',
        ],
        finish: '工具箱可以打开，并能看到 A2A、会话、快捷按钮、Skills 等入口。',
    },
    {
        title: '配置 lechun-a2a MCP',
        lead: 'CodexPlus 会读取管理员发布的最新配置内容，并创建原生 Codex 会话完成配置。',
        actions: [
            '在 A2A 引导中找到 lechun-a2a MCP。',
            '点击“开始配置”，阅读最新配置内容和风险提示。',
            '勾选确认并选择有效的 Codex 项目。',
            '发送配置内容，等待 Codex 执行并由 CodexPlus 自动检查结果。',
        ],
        finish: '登录状态和 lechun-a2a MCP 配置检查全部通过。',
        checks: ['登录状态', 'lechun-a2a MCP'],
        prompt: true,
    },
    {
        title: '发送或接收第一个任务',
        lead: '完成配置后，就可以让团队任务直接进入 Codex 工作流。',
        actions: [
            '在“我执行的”中核查别人派给你的任务。',
            '选择或确认任务对应的本机 Codex 项目。',
            '创建会话并在 Codex 中处理任务。',
            '检查代码和实际结果后，再回到任务卡片点击“确认完成”。',
        ],
        finish: '任务已形成真实 Codex 会话，并能把完成状态同步给派发方。',
        tip: 'Codex 一次回复结束不代表任务已经验收完成。',
    },
]

const FAQ_GROUPS = [
    {
        key: 'install',
        name: '下载安装',
        questions: [
            {
                key: 'architecture',
                title: '如何判断 Apple 芯片和 Intel 芯片？',
                content: <p>点击苹果菜单 → 关于本机。芯片显示 M1、M2、M3、M4 或更新型号时选择 Apple 芯片版；处理器显示 Intel 时选择 Intel 版。</p>,
            },
            {
                key: 'macos-quarantine',
                title: 'macOS 提示“应用已损坏”或“无法打开”怎么办？',
                gatekeeper: true,
            },
            {
                key: 'dmg-run',
                title: '可以直接在 DMG 中运行 CodexPlus 吗？',
                content: <p>不建议。请先将 CodexPlus 拖入“应用程序”，退出安装镜像后，再从“应用程序”启动。</p>,
            },
            {
                key: 'app-in-use',
                title: '更新安装时提示 CodexPlus 正在使用怎么办？',
                content: <p>退出 CodexPlus，并确认菜单栏和程序坞中都没有正在运行的 CodexPlus。若仍提示正在使用，请同时退出官方 Codex 后重试。</p>,
            },
        ],
    },
    {
        key: 'login',
        name: '启动与登录',
        questions: [
            {
                key: 'toolbox-missing',
                title: '安装后为什么看不到 CodexPlus 工具箱？',
                content: <p>完全退出官方 Codex 和 CodexPlus，再从“应用程序”启动 CodexPlus，让它自动打开 Codex。进入后在页面右上方查找工具箱按钮。</p>,
            },
            {
                key: 'ding-login',
                title: '浏览器授权完成后为什么没有自动登录？',
                content: <p>先等待几秒让 CodexPlus 完成授权轮询。如果仍无响应，取消本次授权并重新发起；持续失败时导出诊断报告。</p>,
            },
            {
                key: 'api-key',
                title: '还需要手工设置 API Key 吗？',
                content: <p>不需要。员工账号或钉钉登录成功后，CodexPlus 会自动保存并维护 API Key，凭证不会注入 Codex 页面。</p>,
            },
            {
                key: 'login-report',
                title: '登录失败后应该提供什么信息？',
                content: <p>请提供失败时间、界面错误提示和 CodexPlus 导出的诊断报告。不要发送密码、API Key 或浏览器授权信息。</p>,
            },
        ],
    },
    {
        key: 'a2a',
        name: 'A2A 配置',
        questions: [
            {
                key: 'mcp-failed',
                title: 'lechun-a2a MCP 配置失败怎么办？',
                content: <p>重新打开 A2A 引导，确认登录有效、选择了真实 Codex 项目，并重新读取管理员最新配置。失败时查看配置会话和诊断日志。</p>,
            },
            {
                key: 'task-missing',
                title: '配置完成后为什么仍看不到任务？',
                content: <p>检查 A2A 页面顶部的服务连接状态。没有映射本机项目的任务不会自动创建会话，但仍应保留在任务列表中。</p>,
            },
            {
                key: 'project-overwrite',
                title: '服务端项目会覆盖本机项目吗？',
                content: <p>不会。服务端“所属项目”和用户选择的“Codex 项目”是两个独立字段，通过项目映射建立关联。</p>,
            },
        ],
    },
    {
        key: 'task',
        name: '任务使用',
        questions: [
            {
                key: 'auto-execute',
                title: '收到任务后会自动执行吗？',
                content: <p>默认不会直接执行。可以配置在项目已经映射时自动创建会话；“收到任务后直接执行”属于高风险选项，需要用户主动开启。</p>,
            },
            {
                key: 'task-complete',
                title: 'Codex 回复结束代表任务完成吗？',
                content: <p>不代表。需要执行人检查代码、测试和实际结果后，再主动点击“确认完成”。</p>,
            },
            {
                key: 'change-project',
                title: '如何更换任务对应的本机 Codex 项目？',
                content: <p>在任务卡片中重新选择项目，并按需要保存项目映射。新映射不会自动移动已经创建的会话。</p>,
            },
            {
                key: 'close-delete',
                title: '关闭任务和删除任务有什么区别？',
                content: <p>关闭适用于取消或暂不继续，任务仍可在已关闭筛选中查看；删除会把任务从普通列表隐藏，但不会删除已经创建的 Codex 会话。</p>,
            },
        ],
    },
]

const { Panel } = Collapse
const { Text } = Typography
const MACOS_QUARANTINE_COMMAND = 'xattr -dr com.apple.quarantine "/Applications/CodexPlus.app"'

class A2ATab extends Component {
    state = {
        loading: true,
        portalOverview: null,
        portalLoadFailed: false,
        faqGroup: 'install',
    }

    componentDidMount() {
        this.loadPortalOverview()
    }

    loadPortalOverview = async () => {
        this.setState({ loading: true, portalLoadFailed: false })
        try {
            const portalOverview = await getA2aPortalOverview()
            this.setState({ loading: false, portalOverview: portalOverview || {} })
        } catch (error) {
            // 2026-08-08 coder(lq): 发布中心不可用时明确显示失败，不回退到过期或虚构下载地址。
            this.setState({ loading: false, portalOverview: null, portalLoadFailed: true })
        }
    }

    copyPrompt = () => {
        const prompt = this.state.portalOverview?.prompt?.promptContent || ''
        if (!prompt) {
            message.warning('管理员尚未发布任务发送方 Prompt')
            return
        }
        if (navigator?.clipboard?.writeText) {
            navigator.clipboard.writeText(prompt)
                .then(() => message.success('任务发送方 Prompt 已复制'))
                .catch(() => message.warning('复制失败，请手动选择并复制'))
            return
        }
        message.warning('当前浏览器不支持自动复制，请手动选择并复制')
    }

    downloadAsset = asset => {
        if (!asset?.url) {
            message.warning('该平台安装包尚未发布')
            return
        }
        window.open(asset.url, '_blank')
    }

    scrollTo = id => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    renderPromptFallback = (senderPrompt, portalLoadFailed) => (
        <details className="profile-a2a-manual-prompt">
            <summary>排障备用：查看并手工复制配置 Prompt</summary>
            <p>仅当技术人员要求手工排查时使用。正常情况下请不要重复执行，以免产生重复配置。</p>
            <Spin spinning={this.state.loading} tip="正在加载配置 Prompt">
                <Input.TextArea
                    className="profile-a2a-prompt"
                    readOnly
                    value={senderPrompt}
                    autoSize={{ minRows: 8, maxRows: 16 }}
                    placeholder={portalLoadFailed
                        ? '配置 Prompt 加载失败，请点击“重试”重新加载。'
                        : '管理员尚未发布配置 Prompt。'}
                />
            </Spin>
            <div className="profile-a2a-manual-actions">
                <Button icon={<CopyOutlined />} disabled={!senderPrompt} onClick={this.copyPrompt}>复制备用 Prompt</Button>
                {portalLoadFailed && <Button onClick={this.loadPortalOverview}>重新加载</Button>}
            </div>
        </details>
    )

    renderInstallStep = (step, index, senderPrompt, portalLoadFailed) => (
        <article className="profile-a2a-install-step" key={step.title}>
            <div className="profile-a2a-install-copy">
                <div className="profile-a2a-install-label">
                    <span>{String(index + 1).padStart(2, '0')}</span>
                    安装步骤
                </div>
                <h3>{step.title}</h3>
                <p className="profile-a2a-install-lead">{step.lead}</p>
                <ol>{step.actions.map(action => <li key={action}>{action}</li>)}</ol>
                {step.downloadLink && (
                    <Button size="small" icon={<CloudDownloadOutlined />} onClick={() => this.scrollTo('codexplus-downloads')}>前往下载模块</Button>
                )}
                {step.checks && (
                    <div className="profile-a2a-install-checks">
                        {step.checks.map(check => <span key={check}><CheckOutlined /> {check}</span>)}
                    </div>
                )}
                {step.tip && <div className="profile-a2a-install-tip"><strong>注意：</strong>{step.tip}</div>}
                <div className="profile-a2a-install-finish">
                    <CheckCircleOutlined />
                    <div><strong>完成标志</strong><p>{step.finish}</p></div>
                </div>
                {step.prompt && (
                    <div className="profile-a2a-prompt-fallback">
                        <Alert
                            showIcon
                            type="success"
                            message="推荐方式：在 CodexPlus 中一键配置"
                            description="CodexPlus 会展示配置内容和风险提示，只有你阅读并勾选确认后才会发送给 Codex；检测到 lechun-a2a MCP 后才会显示配置成功。"
                        />
                        {this.renderPromptFallback(senderPrompt, portalLoadFailed)}
                    </div>
                )}
            </div>
        </article>
    )

    renderGatekeeperFaq = () => (
        <>
            <p>请先确认安装包来自公司 A2A 页面。若 macOS 阻止打开 CodexPlus，请打开<strong>系统设置 → 隐私与安全性</strong>，向下滚动到“安全性”部分，在“已阻止 CodexPlus 以保护 Mac”旁点击<strong>仍要打开</strong>，然后按系统提示确认。</p>
            <img className="profile-a2a-gatekeeper-guide" src={macosGatekeeperStillOpen} alt="macOS 隐私与安全性页面中的仍要打开按钮" />
            <p>如果没有看到“仍要打开”，请先尝试启动一次 CodexPlus，再立即回到“隐私与安全性”页面查看。</p>
            <p>如果系统设置方式仍无法解决，可打开 macOS 的“终端”，复制并执行下面的命令，然后重新打开 CodexPlus：</p>
            <div className="profile-a2a-command">
                <Text code copyable={{ text: MACOS_QUARANTINE_COMMAND }}>{MACOS_QUARANTINE_COMMAND}</Text>
            </div>
            <p className="profile-a2a-faq-tip">这条命令只会清除 CodexPlus 的 macOS 隔离标记，不会删除应用或个人数据。</p>
        </>
    )

    render() {
        const { loading, portalOverview, portalLoadFailed, faqGroup } = this.state
        const senderPrompt = portalOverview?.prompt?.promptContent || ''
        const legacyRelease = portalOverview?.release || null
        const apiPlatformReleases = portalOverview?.platformReleases || {}
        // 2026-08-08 coder(lq): 支持前后端分批发布；旧接口期间仍可展示整体版本已有的安装包。
        const platformReleases = Object.keys(apiPlatformReleases).length > 0
            ? apiPlatformReleases
            : DOWNLOADS.reduce((result, download) => {
                const hasAsset = legacyRelease?.assets?.some(asset => asset.platform === download.key)
                if (hasAsset) result[download.key] = legacyRelease
                return result
            }, {})
        const hasAvailableRelease = DOWNLOADS.some(download => platformReleases[download.key])
        const activeFaqGroup = FAQ_GROUPS.find(group => group.key === faqGroup) || FAQ_GROUPS[0]

        return (
            <div className="profile-a2a-wrap">
                <section className="profile-a2a-product-hero">
                    <Tag color="blue">乐纯内部 Codex 效率与协作工具</Tag>
                    <div className="profile-a2a-product-logo"><SendOutlined /></div>
                    <h1>让 Codex 从个人助手<br />升级为团队协作入口</h1>
                    <p className="profile-a2a-product-lead">
                        CodexPlus 是直接运行在官方 Codex 中的桌面增强工具。它不替代 Codex，也不改变原来的使用习惯，
                        而是把团队任务协作、项目关联、原生会话创建、Skills 安装和常用效率工具带进同一个工作界面。
                    </p>
                    <p className="profile-a2a-product-result">
                        从别人把任务交给你，到 Codex 开始处理，再到结果经过人工核查并同步完成，整个过程不再依赖聊天工具、手工复制和口头追踪。
                    </p>
                    <div className="profile-a2a-hero-actions">
                        <Button type="primary" size="large" icon={<CloudDownloadOutlined />} onClick={() => this.scrollTo('codexplus-downloads')}>下载安装 CodexPlus</Button>
                        <Button size="large" icon={<RocketOutlined />} onClick={() => this.scrollTo('codexplus-installation')}>查看完整安装步骤</Button>
                    </div>
                    <div className="profile-a2a-feature-chips">
                        {['A2A 团队任务协作', '原生 Codex 会话', '本机项目映射', '完成状态同步', '公司 Skills 商城', '会话与效率工具'].map(item => (
                            <span key={item}><CheckOutlined /> {item}</span>
                        ))}
                    </div>
                </section>

                <section className="profile-a2a-content-section profile-a2a-value-section">
                    <div className="profile-a2a-content-head is-centered">
                        <span>WHY CODEXPLUS</span>
                        <h2>不再把任务复制给 Codex<br />而是让任务直接抵达 Codex</h2>
                        <p>Codex 已经很擅长完成任务，CodexPlus 解决的是任务如何安全、准确地来到 Codex，以及完成以后如何回到团队。</p>
                    </div>
                    <div className="profile-a2a-value-grid">
                        {PRODUCT_CAPABILITIES.map(item => (
                            <article key={item.title}>
                                <div className="profile-a2a-value-icon">{item.icon}</div>
                                <h3>{item.title}</h3>
                                <p>{item.description}</p>
                            </article>
                        ))}
                    </div>
                </section>

                <section className="profile-a2a-content-section profile-a2a-comparison-section">
                    <div className="profile-a2a-content-head">
                        <span>BEFORE &amp; AFTER</span>
                        <h2>少一次复制，多一条完整的协作链路</h2>
                        <p>每一个过去需要人工搬运和追问的环节，现在都有明确、可核查的流转方式。</p>
                    </div>
                    <div className="profile-a2a-comparison-table">
                        <div className="profile-a2a-comparison-title is-old">以前：任务到了，人还要继续搬运</div>
                        <div className="profile-a2a-comparison-title is-new">现在：任务直接成为 Codex 工作</div>
                        {COMPARISONS.map(([before, after]) => (
                            <div className="profile-a2a-comparison-row" key={before}>
                                <div><span>×</span>{before}</div>
                                <div><CheckOutlined />{after}</div>
                            </div>
                        ))}
                    </div>
                </section>

                <section className="profile-a2a-content-section profile-a2a-flow-section">
                    <div className="profile-a2a-content-head is-centered">
                        <span>HOW IT WORKS</span>
                        <h2>四步进入团队协作流程</h2>
                    </div>
                    <div className="profile-a2a-simple-flow">
                        {SIMPLE_FLOW.map((item, index) => (
                            <article key={item.number}>
                                <span>{item.number}</span>
                                <h3>{item.title}</h3>
                                <p>{item.description}</p>
                                {index < SIMPLE_FLOW.length - 1 && <b><ArrowRightOutlined /></b>}
                            </article>
                        ))}
                    </div>
                    <div className="profile-a2a-trust-note">
                        <SafetyCertificateOutlined />
                        <strong>安全与信任</strong>
                        <span>基于官方 Codex 原生会话和项目机制运行，不直接修改 Codex 数据库；公司账号凭证由 CodexPlus 原生后台维护，不进入 Codex 页面。</span>
                    </div>
                </section>

                <section className="profile-a2a-content-section profile-a2a-installation-section" id="codexplus-installation">
                    <div className="profile-a2a-content-head is-centered">
                        <span>INSTALLATION GUIDE</span>
                        <h2>从下载安装到开始协作</h2>
                        <p>按顺序完成下面 6 个步骤。每一步都有明确的完成标志，A2A 配置检查全部通过后即可开始使用。</p>
                    </div>
                    <div className="profile-a2a-install-list">
                        {INSTALL_STEPS.map((step, index) => this.renderInstallStep(step, index, senderPrompt, portalLoadFailed))}
                    </div>
                </section>

                <section className="profile-a2a-content-section profile-a2a-download-section" id="codexplus-downloads">
                    <div className="profile-a2a-download-head">
                        <div className="profile-a2a-content-head">
                            <span>DOWNLOAD</span>
                            <h2>下载适合你电脑的 CodexPlus</h2>
                            <p>{hasAvailableRelease ? '各平台独立展示已发布的最新版本，请选择自己电脑对应的安装包。' : '管理员尚未发布 CodexPlus 稳定版。'}</p>
                        </div>
                        <Button icon={<CloudDownloadOutlined />} loading={loading} onClick={this.loadPortalOverview}>刷新版本</Button>
                    </div>
                    <Alert
                        showIcon
                        type="info"
                        message="请仅从乐纯 Portal 下载 CodexPlus"
                        description="版本信息、文件名和校验值由公司 A2A 发布中心统一维护，请勿使用聊天记录或未知地址中的历史安装包。"
                    />
                    {portalLoadFailed && <Alert showIcon type="error" message="CodexPlus 版本信息加载失败" description="请检查网络后重试。" action={<Button size="small" onClick={this.loadPortalOverview}>重试</Button>} />}
                    {!portalLoadFailed && !loading && !hasAvailableRelease && <Alert showIcon type="warning" message="暂无可下载版本" description="请联系管理员在 A2A 管理页发布版本。" />}
                    {loading && !portalOverview && <div className="profile-a2a-download-loading"><Spin tip="正在加载版本信息" /></div>}
                    {hasAvailableRelease && (
                        <div className="profile-a2a-download-grid">
                            {DOWNLOADS.map(download => {
                                const platformRelease = platformReleases[download.key]
                                const asset = platformRelease?.assets?.find(item => item.platform === download.key)
                                return (
                                    <article className={`profile-a2a-download-card ${asset ? '' : 'is-unavailable'}`} key={download.key}>
                                        <div className="profile-a2a-download-icon">{download.icon}</div>
                                        <h3>{download.platform}</h3>
                                        <p>{download.detail}</p>
                                        <button type="button" className="profile-a2a-chip-help" onClick={() => this.scrollTo('codexplus-installation')}>如何判断电脑芯片？</button>
                                        <Tag color={platformRelease ? 'blue' : 'default'}>{platformRelease ? `当前版本 ${platformRelease.version}` : '暂无版本'}</Tag>
                                        <small>{asset ? `${asset.name} · SHA-256 ${asset.sha256 || '-'}` : '该平台暂未发布安装包'}</small>
                                        <Button type="primary" disabled={!asset} icon={<CloudDownloadOutlined />} onClick={() => this.downloadAsset(asset)}>
                                            {asset ? '下载安装包' : '暂不可用'}
                                        </Button>
                                    </article>
                                )
                            })}
                        </div>
                    )}
                </section>

                <section className="profile-a2a-content-section profile-a2a-faq-section">
                    <div className="profile-a2a-content-head is-centered">
                        <span>FAQ</span>
                        <h2>遇到问题？先从这里排查</h2>
                        <p>按照问题类型快速定位。仍无法解决时，再导出 CodexPlus 诊断报告交给技术人员。</p>
                    </div>
                    <div className="profile-a2a-faq-tabs" role="tablist">
                        {FAQ_GROUPS.map(group => (
                            <button
                                type="button"
                                role="tab"
                                aria-selected={faqGroup === group.key}
                                className={faqGroup === group.key ? 'is-active' : ''}
                                onClick={() => this.setState({ faqGroup: group.key })}
                                key={group.key}
                            >
                                {group.name}
                            </button>
                        ))}
                    </div>
                    <Collapse bordered={false} className="profile-a2a-faq" expandIconPosition="right" defaultActiveKey={[activeFaqGroup.questions[0].key]} key={activeFaqGroup.key}>
                        {activeFaqGroup.questions.map(question => (
                            <Panel header={question.title} key={question.key}>
                                {question.gatekeeper ? this.renderGatekeeperFaq() : question.content}
                            </Panel>
                        ))}
                    </Collapse>
                </section>
            </div>
        )
    }
}

export default A2ATab
